import React from "react";

/**
 * FeatureImageGrid — luxury image-backed grid for "Kitchen Details", "Storage
 * Types" and similar visual category strips on service landing pages.
 *
 * Each item: { title, desc, image, alt, kicker? }. Images are rendered inside
 * a fixed aspect ratio so the grid stays visually consistent regardless of
 * the source asset. Soft burgundy → deep ink overlay ensures legibility for
 * any caption rendered over the image, while the title + description sit
 * below the image for clean editorial reading flow.
 *
 * Image paths are kept as plain strings so they can be swapped from page data
 * (or, later, from a CMS) without touching this component.
 */
export default function FeatureImageGrid({
  testid,
  eyebrow,
  heading,
  subtext,
  items = [],
  // 2 → desktop columns; defaults to 3, but pass 2 / 4 / 5 to tune density.
  columns = 3,
  // Use a slightly tighter card aspect for "details" grids; 4/5 by default.
  aspect = "aspect-[4/5]",
  bgClass = "bg-[#FAF5EC] border-y border-[#6E1F2C]/15",
}) {
  const colClass =
    columns === 2
      ? "md:grid-cols-2"
      : columns === 4
      ? "md:grid-cols-2 lg:grid-cols-4"
      : columns === 5
      ? "md:grid-cols-2 lg:grid-cols-5"
      : "md:grid-cols-3";

  return (
    <section data-testid={testid} className={bgClass}>
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-20 md:py-28">
        {eyebrow && (
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-5">
            {eyebrow}
          </p>
        )}
        {heading && (
          <h2
            data-testid={`${testid}-heading`}
            className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.08] text-[#1C110C] max-w-3xl"
          >
            {heading}
          </h2>
        )}
        {subtext && (
          <p
            data-testid={`${testid}-subtext`}
            className="mt-6 max-w-2xl text-[#3E2A22] text-base md:text-[17px] leading-[1.85] font-light"
          >
            {subtext}
          </p>
        )}

        <div
          data-testid={`${testid}-grid`}
          className={`mt-12 md:mt-14 grid grid-cols-1 sm:grid-cols-2 ${colClass} gap-6 md:gap-8`}
        >
          {items.map((it, i) => (
            <div
              key={it.title}
              data-testid={`${testid}-item-${i}`}
              className="group flex flex-col"
            >
              <div className={`relative ${aspect} overflow-hidden bg-[#1C110C]/[0.04]`}>
                <img
                  src={it.image}
                  alt={it.alt || it.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C110C]/55 via-[#1C110C]/10 to-transparent pointer-events-none" />
                {it.kicker && (
                  <span
                    data-testid={`${testid}-kicker-${i}`}
                    className="absolute top-5 left-5 text-[10px] tracking-[0.32em] uppercase text-[#FAF5EC]/90 bg-[#1C110C]/40 backdrop-blur-sm px-3 py-1.5 border border-[#FAF5EC]/15"
                  >
                    {it.kicker}
                  </span>
                )}
              </div>
              <h3 className="mt-6 font-serif-display text-xl md:text-2xl text-[#1C110C] leading-snug">
                {it.title}
              </h3>
              {it.desc && (
                <p className="mt-3 text-[#3E2A22] leading-[1.75] text-sm md:text-base font-light">
                  {it.desc}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
